/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";

// owner
import searchUIClient from "@/apis/searchUIClient";
import { forceMapFilters } from "@cleanup/components/AdvancedFilters/FilterConstants";
import { CircleGroup } from "@/components/Leaflet/Map/Marker/CircleGroup";

interface DeviceData {
  id: number;
  name: string;
  currentLat: number;
  currentLng: number;
  lastSeenAlive?: string;
  lastNetworkLocationRadius?: number;
  decoratorColor?: string;
  location?: string;
  lastBeaconName?: string;
  lastActivity?: string;
  coordinate?: {
    lat: number;
    lon: number;
    lastSeen?: string;
  };
}

interface SearchQuery {
  searchQueryType: string;
  queryObjectType: string;
  query: Array<{
    categoryId: string;
    filterKey: string;
    filterType: string;
    searchType: string;
    notFilter: boolean;
    filterValue: {
      includeEmpty: boolean;
      includeNoEmpty: boolean;
      selectedValues: unknown[];
    };
  }>;
  requestedTableColumns: Array<{
    columnKey: string;
    order: null;
  }>;
  selectedOrgId?: number;
}

const NetworkLayerClass = (L.LayerGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,

  updateLayer: function (layer: L.Layer | null) {
    const self = this;

    this.clearLayers();
    if (layer) {
      self.addLayer(layer);
    }
  },

  updateData: function (orgId: number | null) {
    const self = this;
    this.clearLayers();

    const basic: SearchQuery = {
      searchQueryType: "BASIC",
      queryObjectType: "SIGFOX_DEVICE",
      query: [
        {
          categoryId: "locationInfo",
          filterKey: "locationService_lastLocationSource",
          filterType: "MULTI_SELECT_CHECKBOX",
          searchType: "STATIC",
          notFilter: false,
          filterValue: {
            includeEmpty: false,
            includeNoEmpty: false,
            selectedValues: ["network"],
          },
        },
      ],
      requestedTableColumns: [
        {
          columnKey: "lastNetworkLocationRadius",
          order: null,
        },
        {
          columnKey: "currentLat",
          order: null,
        },
        {
          columnKey: "currentLng",
          order: null,
        },
        {
          columnKey: "name",
          order: null,
        },
        {
          columnKey: "decoratorColor",
          order: null,
        },
        {
          columnKey: "location",
          order: null,
        },
        {
          columnKey: "lastBeaconName",
          order: null,
        },
        {
          columnKey: "lastActivity",
          order: null,
        },
      ],
    };

    if (orgId) {
      basic.selectedOrgId = orgId;
    }

    forceMapFilters(basic as any, false);

    void searchUIClient.search(basic as any).then(({ data }: any) => {
      self.processData(data.data);
    });
  },

  transformData: function (beacon: DeviceData) {
    beacon.coordinate = {
      lat: beacon.currentLat,
      lon: beacon.currentLng,
      lastSeen: beacon.lastSeenAlive,
    };
  },

  isValidCoordinate: function (
    coordinate: { lat: number; lon: number } | undefined
  ): boolean {
    return !!(coordinate && (coordinate.lat || coordinate.lon));
  },

  processData: function (data: DeviceData[]) {
    const self = this;
    const lastMarkers: DeviceData[] = [];
    data.forEach((obj: DeviceData) => {
      self.transformData(obj);
      const coordinate = obj.coordinate;
      const isValid = self.isValidCoordinate(coordinate);
      if (isValid) {
        lastMarkers.push(obj);
      }
    });
    self.processMarkers(lastMarkers);
  },

  processMarkers: function (lastMarkers: DeviceData[]) {
    const self = this;

    const circles: Array<{ center: L.LatLng; radius: number }> = [];
    lastMarkers.forEach((item: DeviceData) => {
      if (!item.coordinate) return;
      const _radius = item.lastNetworkLocationRadius || 0;
      circles.push({
        center: L.latLng(item.coordinate.lat, item.coordinate.lon),
        radius: _radius,
      });
    });

    if (circles.length) {
      const layer = CircleGroup(circles, {
        color: "#5094BE",
        fillOpacity: 0.5,
        opacity: 0,
      });
      self.addLayer(layer);
    }
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).NetworkLayer = NetworkLayerClass;
(L as any).networkLayer = function (opts?: L.LayerOptions): any {
  return new NetworkLayerClass(opts);
};

export const layerNetwork = (L as any).networkLayer;
